import { useEffect, useState } from 'react';
import { Button, ErrorState, Input, PageHeader, PageLoader, Select, Switch, Textarea, useToast } from '../../../components/ui';
import { FormSection } from '../../../components/common/Parts/Parts';
import { useFetch } from '../../../hooks/useFetch';
import { withHomeState } from '../../../constants/location';
import { GSTIN_RX, serverFieldErrors } from '../../../utils/validation';
import { useForm } from '../../../hooks/useForm';
import { SignatureInput } from '../components/SignatureInput/SignatureInput';
import { StampInput } from '../components/StampInput/StampInput';
import { clearSettingsCache, settingsService } from '../services/settingsService';
import type { ChangeEvent } from 'react';
import type { CompanySettings } from '../types/settingsTypes';
import type { FormEvent } from 'react';
import type { FormErrors } from '../../../hooks/useForm';

const PREFIXES = [
  ['quotation', 'Quotation'],
  ['service', 'Service job'],
  ['gstInvoice', 'GST invoice'],
  ['nonGstInvoice', 'Non-GST invoice'],
  ['purchase', 'Purchase'],
  ['expense', 'Expense'],
  ['transaction', 'Payment'],
  ['customer', 'Customer'],
  ['vendor', 'Supplier'],
  ['item', 'Material / work rate'],
];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => ({ value: i + 1, label: m }));

const fromSettings = (s: CompanySettings) => ({
  companyName: s.companyName ?? '', phone: s.phone ?? '', email: s.email ?? '', website: s.website ?? '', gstin: s.gstin ?? '', pan: s.pan ?? '',
  line: s.address?.line ?? '', city: s.address?.city ?? '', pincode: s.address?.pincode ?? '',
  accountName: s.bank?.accountName ?? '', bankName: s.bank?.bankName ?? '', accountNo: s.bank?.accountNo ?? '', ifsc: s.bank?.ifsc ?? '', upiId: s.bank?.upiId ?? '',
  prefixes: { ...(s.prefixes ?? {}) },
  financialYearStartMonth: s.financialYearStartMonth ?? 4, quotationValidityDays: String(s.quotationValidityDays ?? 15), invoiceDueDays: String(s.invoiceDueDays ?? 0),
  quotationTerms: s.quotationTerms ?? '', invoiceTerms: s.invoiceTerms ?? '',
  signatureImage: s.signatureImage ?? '', signOnQuotations: s.signOnQuotations ?? true, signOnInvoices: s.signOnInvoices ?? true,
  stampImage: s.stampImage ?? '', stampOnQuotations: s.stampOnQuotations ?? true, stampOnInvoices: s.stampOnInvoices ?? true,
});

export default function Settings() {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const { form, setForm, set, errors, setErrors } = useForm<ReturnType<typeof fromSettings> | null>(null);
  const { data, loading, error, reload } = useFetch(() => settingsService.get(), []);
  useEffect(() => {
    if (data) setForm(fromSettings(data));
  }, [data, setForm]);

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (loading || !form) return <PageLoader />;

  const digits = (key: keyof ReturnType<typeof fromSettings>) => (e: ChangeEvent<HTMLInputElement>) => set(key)(e.target.value.replace(/\D/g, ''));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: FormErrors = {};
    if (!form.companyName.trim()) errs.companyName = 'Enter the company name';
    if (form.gstin && !GSTIN_RX.test(form.gstin)) errs.gstin = 'GSTIN should be 15 characters, like 27ABCDE1234F1Z5';
    if (form.gstin && !form.gstin.startsWith('27')) errs.gstin = 'A Maharashtra GSTIN starts with 27';
    if (!(Number(form.quotationValidityDays) >= 1)) errs.quotationValidityDays = 'At least 1 day';
    PREFIXES.forEach(([k]) => {
      if (!/^[A-Za-z0-9-]{1,10}$/.test(form.prefixes[k] ?? '')) errs[`prefixes.${k}`] = 'Letters, numbers and - only';
    });
    setErrors(errs);
    if (Object.keys(errs).length) return toast.error('Please fix the highlighted fields');

    setSaving(true);
    try {
      const saved = await settingsService.update({
        companyName: form.companyName.trim(), phone: form.phone.trim(), email: form.email.trim(), website: form.website.trim(),
        gstin: form.gstin.trim(), pan: form.pan.trim().toUpperCase(),
        address: withHomeState({ line: form.line.trim(), city: form.city.trim(), pincode: form.pincode.trim() }),
        bank: { accountName: form.accountName.trim(), bankName: form.bankName.trim(), accountNo: form.accountNo.trim(), ifsc: form.ifsc.trim(), upiId: form.upiId.trim() },
        prefixes: form.prefixes,
        financialYearStartMonth: Number(form.financialYearStartMonth),
        quotationValidityDays: Number(form.quotationValidityDays),
        invoiceDueDays: Number(form.invoiceDueDays) || 0,
        quotationTerms: form.quotationTerms, invoiceTerms: form.invoiceTerms,
        signatureImage: form.signatureImage, signOnQuotations: form.signOnQuotations, signOnInvoices: form.signOnInvoices,
        stampImage: form.stampImage, stampOnQuotations: form.stampOnQuotations, stampOnInvoices: form.stampOnInvoices,
      });
      clearSettingsCache();
      setForm(fromSettings(saved));
      toast.success('Settings saved');
    } catch (err) {
      setErrors(serverFieldErrors(err));
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const save = <Button type="submit" form="settings-form" leftIcon="check" loading={saving}>Save settings</Button>;

  return (
    <>
      <PageHeader title="Settings" subtitle="Company details printed on quotations and invoices" actions={save} />
      <form id="settings-form" onSubmit={submit} noValidate className="space-y-5">
        <FormSection title="Company">
          <Input label="Company name" required value={form.companyName} onChange={set('companyName')} error={errors.companyName} containerClassName="sm:col-span-2" />
          <Input label="Phone" type="tel" value={form.phone} onChange={set('phone')} />
          <Input label="Email" type="email" value={form.email} onChange={set('email')} error={errors.email} />
          <Input label="Website" value={form.website} onChange={set('website')} />
          <Input label="GSTIN" value={form.gstin} onChange={(e) => set('gstin')(e.target.value.toUpperCase())} error={errors.gstin} maxLength={15} hint="Needed for GST quotations and invoices" />
          <Input label="PAN" value={form.pan} onChange={(e) => set('pan')(e.target.value.toUpperCase())} maxLength={10} />
        </FormSection>

        <div className="grid gap-5 xl:grid-cols-2">
          <FormSection title="Address" subtitle="Maharashtra">
            <Textarea label="Shop / street" rows={2} value={form.line} onChange={set('line')} containerClassName="sm:col-span-2" />
            <Input label="City" value={form.city} onChange={set('city')} />
            <Input label="Pincode" inputMode="numeric" maxLength={6} value={form.pincode} onChange={digits('pincode')} />
          </FormSection>
          <FormSection title="Bank details" subtitle="Printed so customers can pay you">
            <Input label="Account name" value={form.accountName} onChange={set('accountName')} />
            <Input label="Bank name" value={form.bankName} onChange={set('bankName')} />
            <Input label="Account number" value={form.accountNo} onChange={digits('accountNo')} />
            <Input label="IFSC" value={form.ifsc} onChange={(e) => set('ifsc')(e.target.value.toUpperCase())} maxLength={11} />
            <Input label="UPI ID" value={form.upiId} onChange={set('upiId')} containerClassName="sm:col-span-2" />
          </FormSection>
        </div>

        <FormSection title="Numbers and defaults" bodyClassName="sm:grid-cols-3">
          <Select label="Financial year starts in" options={MONTHS} value={Number(form.financialYearStartMonth)} onChange={set('financialYearStartMonth')} searchable={false} />
          <Input label="Quotation valid for (days)" inputMode="numeric" value={form.quotationValidityDays} onChange={digits('quotationValidityDays')} error={errors.quotationValidityDays} />
          <Input label="Invoice due after (days)" inputMode="numeric" value={form.invoiceDueDays} onChange={digits('invoiceDueDays')} hint="0 = due on invoice date" />
          {PREFIXES.map(([k, label]) => (
            <Input
              key={k}
              label={`${label} prefix`}
              value={form.prefixes[k] ?? ''}
              onChange={(e) => setForm((f) => f && { ...f, prefixes: { ...f.prefixes, [k]: e.target.value.toUpperCase() } })}
              error={errors[`prefixes.${k}`]}
              maxLength={10}
            />
          ))}
        </FormSection>

        <FormSection title="Signature" subtitle="Printed above Authorised signature on quotations and invoices" bodyClassName="sm:grid-cols-[minmax(0,1fr)_280px]">
          <div>
            <SignatureInput value={form.signatureImage} onChange={set('signatureImage')} />
            {errors.signatureImage && <p className="mt-2 text-[13px] text-red-600">{errors.signatureImage}</p>}
            <p className="mt-2 text-[13px] text-ink-400">Sign in dark ink on white paper and take a clear photo, or draw it here. The background is removed automatically.</p>
          </div>
          <div className="space-y-3">
            <Switch label="Print on quotations" checked={form.signOnQuotations} onChange={set('signOnQuotations')} className="rounded-lg border border-line p-3" />
            <Switch label="Print on invoices" checked={form.signOnInvoices} onChange={set('signOnInvoices')} className="rounded-lg border border-line p-3" />
          </div>
        </FormSection>

        <FormSection title="Company stamp" subtitle="Printed beside the signature on quotations and invoices" bodyClassName="sm:grid-cols-[minmax(0,1fr)_280px]">
          <div>
            <StampInput value={form.stampImage} onChange={set('stampImage')} />
            {errors.stampImage && <p className="mt-2 text-[13px] text-red-600">{errors.stampImage}</p>}
            <p className="mt-2 text-[13px] text-ink-400">Press the stamp firmly on plain white paper and take a clear, straight photo in good light. The paper is removed automatically and the ink colour is kept.</p>
          </div>
          <div className="space-y-3">
            <Switch label="Print on quotations" checked={form.stampOnQuotations} onChange={set('stampOnQuotations')} className="rounded-lg border border-line p-3" />
            <Switch label="Print on invoices" checked={form.stampOnInvoices} onChange={set('stampOnInvoices')} className="rounded-lg border border-line p-3" />
          </div>
        </FormSection>

        <FormSection title="Terms">
          <Textarea label="Quotation terms" rows={5} value={form.quotationTerms} onChange={set('quotationTerms')} maxLength={3000} showCount />
          <Textarea label="Invoice terms" rows={5} value={form.invoiceTerms} onChange={set('invoiceTerms')} maxLength={3000} showCount />
        </FormSection>
        <div className="flex justify-end">{save}</div>
      </form>
    </>
  );
}
